import { useMemo } from "react";

import { ApolloQueryResult } from "@apollo/client";
import { ColDef } from "ag-grid-community";
import { message } from "antd";
import { useTranslation } from "react-i18next";

import {
  AlarmConfigurationDto,
  AlarmTypeEnum,
  GetAlarmConfigurationsQuery,
  NotificationTypeEnum,
  useUpdateAlarmConfigurationMutation,
} from "graphql/main";

const useAlarmsConfigurationColDefs = (
  refetchAlarms?: () => Promise<ApolloQueryResult<GetAlarmConfigurationsQuery>>,
) => {
  const [t] = useTranslation();
  const [updateAlarm] = useUpdateAlarmConfigurationMutation({
    onCompleted: () => {
      message.success(t("alarm_configuration_updated_successfully"));
      refetchAlarms?.();
    },
  });
  const localizedNotificationTypes = useMemo(
    () => ({
      [NotificationTypeEnum.Email]: t("email"),
      [NotificationTypeEnum.None]: t("none"),
      [NotificationTypeEnum.Sms]: t("sms"),
    }),
    [t],
  );

  const localizedAlarmTypes = useMemo(
    () => ({
      [AlarmTypeEnum.Bool]: t("bool"),
      [AlarmTypeEnum.CommunicationLost]: t("communication_lost"),
      [AlarmTypeEnum.ContractExpired]: t("contract_expired"),
      [AlarmTypeEnum.CountChange]: t("count_change"),
      [AlarmTypeEnum.ValueGreaterThan]: t("value_greater_than"),
      [AlarmTypeEnum.ValueLessThan]: t("value_less_than"),
      [AlarmTypeEnum.ValueTimestamp]: t("value_timestamp"),
    }),
    [t],
  );

  const columnDefs = useMemo<ColDef<AlarmConfigurationDto>[]>(
    () => [
      {
        editable: true,
        field: "slotName",
        headerName: t("slotName"),
        maxWidth: 150,
        onCellValueChanged: async (params) => {
          await updateAlarm({
            variables: {
              input: { id: params.data.id, slotName: params.newValue },
            },
          });
        },
      },
      {
        field: "code",
        headerName: t("code"),
        maxWidth: 100,
      },
      {
        field: "description",
        headerName: t("description"),
      },
      {
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: Object.values(AlarmTypeEnum) },
        editable: true,
        field: "alarmType",
        filter: false,
        headerName: t("alarmType"),
        maxWidth: 250,
        onCellValueChanged: async (params) => {
          await updateAlarm({
            variables: {
              input: { alarmType: params.newValue, id: params.data.id },
            },
          });
        },
        valueFormatter: (params) =>
          localizedAlarmTypes[
            params.value as keyof typeof localizedAlarmTypes
          ] || params.value,
      },
      {
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: [true, false] },
        cellRenderer: "agSelectCellRenderer",
        editable: true,
        field: "alarmCondition",
        headerName: t("alarmCondition"),
        maxWidth: 150,
        onCellValueChanged: async (params) => {
          await updateAlarm({
            variables: {
              input: { alarmCondition: params.newValue, id: params.data.id },
            },
          });
        },
      },
      {
        cellEditor: "agNumberCellEditor",
        cellEditorParams: { allowNegative: false, precision: 2 },
        editable: true,
        field: "alarmValue",
        headerName: t("alarmValue"),
        maxWidth: 150,
        onCellValueChanged: async (params) => {
          const numericValue = Number(params.newValue);
          if (!isNaN(numericValue)) {
            await updateAlarm({
              variables: {
                input: { alarmValue: numericValue, id: params.data.id },
              },
            });
          }
        },
      },
      {
        cellEditor: "agNumberCellEditor",
        cellEditorParams: { allowNegative: false, precision: 2 },
        editable: true,
        field: "alarmMinutesPeriod",
        headerName: t("alarmMinutesPeriod"),
        maxWidth: 150,
        onCellValueChanged: async (params) => {
          const numericValue = Number(params.newValue);
          if (!isNaN(numericValue)) {
            await updateAlarm({
              variables: {
                input: { alarmMinutesPeriod: numericValue, id: params.data.id },
              },
            });
          }
        },
      },
      {
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: Object.values(NotificationTypeEnum) },
        editable: true,
        field: "notificationType",
        filter: false,
        headerName: t("notification_type"),
        maxWidth: 150,
        onCellValueChanged: async (params) => {
          await updateAlarm({
            variables: {
              input: { id: params.data.id, notificationType: params.newValue },
            },
          });
        },
        valueFormatter: (params) =>
          localizedNotificationTypes[
            params.value as keyof typeof localizedNotificationTypes
          ] || params.value,
      },
    ],
    [localizedAlarmTypes, localizedNotificationTypes, t, updateAlarm],
  );

  return { columnDefs };
};

export default useAlarmsConfigurationColDefs;
